// An item's choices, priced as the till sells them (Martin, 27 Oct). A choose-one group's
// option (a size, a measure, a milk) is entered as its own till price: Bottle £20.00, not
// "+£13.00 on the 175ml". That's what a price list says and what the till already shows on
// the cell. The default option is the item's price. An extras group stays "+£": an extra
// goes on top of whatever's chosen.
//
// What's stored doesn't change: an option's `price` is still the difference from the item's
// price, which the till and the server read. These convert at the edges: `withTillPrices` when
// the dialog opens (each choose-one option gets `till`), `fromTillPrices` when it saves. In
// between, the till prices hold still, so changing the item's price moves the default and
// nothing else.

const round2 = (n) => Math.round(Number(n) * 100) / 100;
const num = (v) => (v === '' || v == null || Number.isNaN(Number(v)) ? 0 : Number(v));
// A till price as the field shows it: pounds and pence, "20.00".
const pounds = (n) => round2(n).toFixed(2);
// A blank cell is "not set", not £0.00. `num('')` is 0, and `??` doesn't catch '', so an
// option whose price was cleared to retype it saved as the item's price *less* the base: the
// till then sold it for nothing. Blank means "leave it as it was stored".
const tillOf = (o, base) => (o.till === '' || o.till == null ? num(base) + num(o.price) : num(o.till));

export const isChooseOne = (g) => g?.rule !== 'any';

/** Each choose-one option with `till`, its price on the till: the item's price, plus its
 *  difference. An extra's "adds" shows in pounds and pence too: 2.00, not 2. */
export function withTillPrices(groups, base) {
  // The default is shown at the price it sells for, like every other option. Pinning it to
  // the item's price told the truth only when its stored difference was zero: an office that
  // had made the 250ml the default (stored +2.60) saw £7.00 on a glass the till charges
  // £9.60 for, and a save with no edit wrote that £7.00 back.
  return (groups || []).map((g) => (isChooseOne(g)
    ? { ...g, options: (g.options || []).map((o) => ({ ...o, till: pounds(num(base) + num(o.price)) })) }
    : { ...g, options: (g.options || []).map((o) => ({ ...o, price: pounds(num(o.price)) })) }));
}

/** Back to what's stored: every choose-one option's difference from the item's price, the
 *  default's included. Forcing the default's to 0 repriced what the till charges for it —
 *  the server bills a chosen option's `price` whether or not it is the default. */
export function fromTillPrices(groups, base) {
  return (groups || []).map((g) => (isChooseOne(g)
    ? { ...g, options: (g.options || []).map(({ till, ...o }) => ({ ...o, price: round2(tillOf({ ...o, till }, base) - num(base)) })) }
    : g));
}

/** Sizes as they are stored: each at its own price, and a cell left blank meaning the item's
 *  price — which is what a blank meant before sizes were typed at their own price. */
export function sizesForSave(sizes, base) {
  return (sizes || []).filter((s) => s.name).map((s) => ({
    name: s.name,
    price: round2(s.price === '' || s.price == null || Number.isNaN(Number(s.price)) ? num(base) : Number(s.price)),
  }));
}

/** Make option `k` the default: it takes the item's price, so the item's price becomes its till
 *  price; the old default keeps the price it had (the item's, until now). */
export function chooseDefault(group, k, base) {
  const next = group.options[k];
  const newBase = next.default ? num(base) : tillOf(next, base);
  return {
    group: { ...group, options: group.options.map((o, m) => ({ ...o, default: m === k, till: o.till ?? pounds(tillOf(o, base)) })) },
    base: round2(newBase),
  };
}

/** Switching a group between choose-one and extras keeps what each option sells for. */
export function switchRule(group, rule, base) {
  if ((rule === 'any') === !isChooseOne(group)) return group;
  if (rule === 'any') {
    return { ...group, rule, options: group.options.map(({ till, ...o }) => ({ ...o, default: false, price: round2(tillOf({ ...o, till }, base) - num(base)) })) };
  }
  // Each option keeps what it sells for, the first one included: making it the default used to
  // drop the amount it added, so a library modifier added as the first option lost its price.
  const options = group.options.map((o, m) => ({ ...o, default: m === 0, till: pounds(num(base) + num(o.price)) }));
  return { ...group, rule, options };
}

/** The item's price changed from `from` to `to`: a choose-one option priced the same as the
 *  item (Medium, at no extra) moves with it; one with a price of its own (the bottle, the
 *  250ml) holds still. Typing the price a digit at a time moves the same options each step. */
export function followItemPrice(groups, from, to) {
  const was = round2(num(from));
  const now = round2(num(to));
  return (groups || []).map((g) => (isChooseOne(g)
    ? { ...g, options: (g.options || []).map((o) => (o.till !== undefined && o.till !== '' && round2(num(o.till)) === was ? { ...o, till: pounds(now) } : o)) }
    : g));
}

/** The item's price typed, from the price field or from the default option's own cell: the
 *  options that follow it follow, and the default's cell, when that is where it was typed,
 *  takes the new price in the same change. Done in two changes, the second built from the
 *  groups as they were, the follow was thrown away: type 28 into Medium's cell and Rare stayed
 *  at £26, saved as £2 under Medium (review of #294). */
export function typeItemPrice(groups, from, to, { follow = true, till = null } = {}) {
  const moved = follow ? followItemPrice(groups, from, to) : groups;
  if (!till) return moved;
  const [gi, oi] = till;
  return moved.map((g, j) => (j === gi ? { ...g, options: g.options.map((o, m) => (m === oi ? { ...o, till: to } : o)) } : g));
}
